/**
 * Workflow Saved List View (Repository Pattern Compliant)
 *
 * Displays user's saved/finalized workflow definitions stored in DynamoDB.
 * Data-driven from backend via WorkflowDraftRepository.
 *
 * Architecture:
 * - Storage: DynamoDB (permanent, no TTL)
 * - Repository: WorkflowDraftRepository.listWorkflowDefinitions()
 * - Actions: Open, Edit (creates draft), Archive
 *
 * Compliance:
 * - Repository Pattern: NO direct GraphQL calls (FERP mandate)
 * - Audit Trail: All operations logged via BaseRepository
 * - Safety Validation: Life-critical operation checks
 *
 * @see CLAUDE.md Line 24: "Repository pattern mandatory"
 * @see useWorkflowDrafts.ts for repository hook implementation
 */

import React from 'react';
import {
    Badge,
    Button,
    Card,
    Group,
    Stack,
    Table,
    Text,
    Title,
    ActionIcon,
    Tooltip,
    Alert,
    Loader
} from '@mantine/core';
import {
    IconEye,
    IconEdit,
    IconArchive,
    IconAlertCircle,
    IconDatabase
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useWorkflowDrafts } from '../hooks/useWorkflowDrafts';
import type { DraftRecord } from '../data/repositories/WorkflowDraftRepository';

interface WorkflowSavedListViewProps {
    tenant: string;
    onOpenWorkflow?: (workflow: DraftRecord) => void;
    onEditWorkflow?: (workflow: DraftRecord) => void;
    onArchiveWorkflow?: (workflow: DraftRecord) => void;
}

export const WorkflowSavedListView: React.FC<WorkflowSavedListViewProps> = ({
    tenant,
    onOpenWorkflow,
    onEditWorkflow,
    onArchiveWorkflow
}) => {
    // ✅ REPOSITORY PATTERN: Use hook that wraps WorkflowDraftRepository
    // This provides audit trails and safety validation
    const { drafts: workflows, loading, error, refresh } = useWorkflowDrafts({
        kind: 'WorkflowDefinition', // Only load WorkflowDefinition kind
        enableAutoRefresh: false,
    });

    // Format timestamp for display
    const formatTimestamp = (ms: number) => {
        return new Date(ms).toLocaleString();
    };

    const handleOpen = (workflow: DraftRecord) => {
        if (onOpenWorkflow) {
            onOpenWorkflow(workflow);
        } else {
            notifications.show({
                title: 'Open Workflow',
                message: `Opening workflow: ${workflow.key}`,
                color: 'blue'
            });
        }
    };

    const handleEdit = (workflow: DraftRecord) => {
        if (onEditWorkflow) {
            onEditWorkflow(workflow);
        } else {
            notifications.show({
                title: 'Edit Workflow',
                message: `Creating draft from workflow: ${workflow.key}`,
                color: 'orange'
            });
        }
    };

    const handleArchive = (workflow: DraftRecord) => {
        if (onArchiveWorkflow) {
            onArchiveWorkflow(workflow);
        } else {
            notifications.show({
                title: 'Archive Workflow',
                message: `Archiving workflow: ${workflow.key}`,
                color: 'gray'
            });
        }
        refresh();
    };

    if (loading) {
        return (
            <Card withBorder>
                <Group justify="center" p="xl">
                    <Loader size="sm" />
                    <Text c="dimmed">Loading saved workflows from DynamoDB...</Text>
                </Group>
            </Card>
        );
    }

    if (error) {
        return (
            <Card withBorder>
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error Loading Workflows">
                    {error.message}
                </Alert>
                <Button mt="md" onClick={() => refresh()}>Retry</Button>
            </Card>
        );
    }

    if (!workflows || workflows.length === 0) {
        return (
            <Card withBorder>
                <Stack ta="center" p="xl">
                    <IconDatabase size={48} opacity={0.3} />
                    <Title order={4}>No Saved Workflows Yet</Title>
                    <Text size="sm" c="dimmed" ta="center">
                        Promote a draft to save it permanently to DynamoDB.
                        <br />
                        Saved workflows are stored without TTL and persist indefinitely.
                    </Text>
                </Stack>
            </Card>
        );
    }

    return (
        <Card withBorder>
            <Group justify="space-between" mb="md">
                <Group>
                    <Title order={4}>My Saved Workflows</Title>
                    <Badge variant="light" color="green">
                        Permanent (DynamoDB)
                    </Badge>
                </Group>
                <Button
                    variant="light"
                    size="xs"
                    onClick={() => refresh()}
                >
                    Refresh
                </Button>
            </Group>

            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Workflow Key</Table.Th>
                        <Table.Th>Owner</Table.Th>
                        <Table.Th>Last Updated</Table.Th>
                        <Table.Th>Updated By</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {workflows.map((workflow) => (
                        <Table.Tr key={workflow.key}>
                            <Table.Td>
                                <Text fw={500}>{workflow.key}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{workflow.owner || 'Unknown'}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{formatTimestamp(workflow.updatedMs)}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{workflow.updatedBy}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Group gap="xs" justify="flex-end">
                                    <Tooltip label="Open workflow (read-only)">
                                        <ActionIcon
                                            variant="light"
                                            color="blue"
                                            onClick={() => handleOpen(workflow)}
                                        >
                                            <IconEye size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Edit workflow (creates new draft)">
                                        <ActionIcon
                                            variant="light"
                                            color="orange"
                                            onClick={() => handleEdit(workflow)}
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Archive workflow">
                                        <ActionIcon
                                            variant="light"
                                            color="gray"
                                            onClick={() => handleArchive(workflow)}
                                        >
                                            <IconArchive size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>

            <Text size="xs" c="dimmed" mt="sm">
                Showing {workflows.length} saved workflow{workflows.length !== 1 ? 's' : ''} • Permanent storage in DynamoDB
            </Text>
        </Card>
    );
};
